import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationExtras, ParamMap, Router as AngularRouter } from '@angular/router';
import { SearchParameters, SortOrder, SortBy } from './searcher.types';
import { ITEMS_PER_PAGE_CONTENT } from '../../../content/retailer-platform/content/constants';

@Injectable()
export class RouterHandler {
    public readonly itemsPerPage = 50;
    public readonly itemsPerPageProductImages = ITEMS_PER_PAGE_CONTENT;

    constructor(
        private router: AngularRouter,
        private route: ActivatedRoute
    ) {}

    public mapUrlParams(paramMap: ParamMap, isContent?: boolean): SearchParameters {
        return {
            query: paramMap.get('query'),
            pagination: {
                offset: _.parseInt(paramMap.get('offset')) || 0,
                limit: isContent ? this.itemsPerPageProductImages : this.itemsPerPage
            },
            filters: {
                ...(paramMap.has('brands') ? { brands: paramMap.getAll('brands') } : {}),
                ...(paramMap.has('brand') ? { brand: paramMap.get('brand') } : {}),
                ...(paramMap.has('categories') ? { categories: paramMap.getAll('categories') } : {}),
                ...(paramMap.has('seasons') ? { seasons: paramMap.getAll('seasons') } : {}),
                ...(paramMap.has('subSeasons') ? { subSeasons: paramMap.getAll('subSeasons') } : {}),
                ...(paramMap.has('ageGroups') ? { ageGroups: paramMap.getAll('ageGroups') } : {}),
                ...(paramMap.has('targetGroups') ? { targetGroups: paramMap.getAll('targetGroups') } : {}),
                ...(paramMap.has('fits') ? { fits: paramMap.getAll('fits') } : {}),
                ...(paramMap.has('colors') ? { colors: paramMap.getAll('colors') } : {}),
                ...(paramMap.has('activeStatuses') ? { activeStatuses: paramMap.getAll('activeStatuses') } : {}),
                ...(paramMap.has('catalogs') ? { catalogs: paramMap.getAll('catalogs') } : {}),
                ...(paramMap.has('minimumDiscount') ? { minimumDiscount: paramMap.get('minimumDiscount') } : {}),
                ...(paramMap.has('minimumStock') ? { minimumStock: paramMap.get('minimumStock') } : {}),
                ...(paramMap.has('sizes') ? { sizes: paramMap.getAll('sizes') } : {}),
                ...(paramMap.has('generalSizes') ? { generalSizes: paramMap.getAll('generalSizes') } : {}),
                ...(paramMap.has('widths') ? { widths: paramMap.getAll('widths') } : {}),
                ...(paramMap.has('lengths') ? { lengths: paramMap.getAll('lengths') } : {}),
                ...(paramMap.has('deliveryDates') ? { deliveryDates: paramMap.getAll('deliveryDates') } : {}),
                ...(paramMap.has('mainCategory') ? { mainCategory: paramMap.get('mainCategory') } : {}),
                ...(paramMap.has('level1Category') ? { level1Category: paramMap.get('level1Category') } : {}),
                ...(paramMap.has('retailers') ? { retailers: paramMap.getAll('retailers') } : {}),
                ...(paramMap.has('orderTypes') ? { orderTypes: paramMap.getAll('orderTypes') } : {}),
                ...(paramMap.has('orderDates') ? { orderDates: paramMap.get('orderDates') } : {}),
                ...(paramMap.has('hasSustainabilityAttributes')
                    ? { hasSustainabilityAttributes: paramMap.get('hasSustainabilityAttributes') === 'true' }
                    : {}),
                ...(paramMap.has('orderedArticles')
                    ? { orderedArticles: paramMap.get('orderedArticles') === 'true' }
                    : {}),
                ...(paramMap.has('awaitingInitialDelivery')
                    ? { awaitingInitialDelivery: paramMap.get('awaitingInitialDelivery') === 'true' }
                    : {}),
                ...(paramMap.has('period') ? { period: paramMap.get('period') } : {})
            },
            sortingCriteria:
                paramMap.has('sortBy') && paramMap.has('sortOrder')
                    ? {
                          sortBy: paramMap.get('sortBy') as SortBy,
                          sortOrder: paramMap.get('sortOrder') as SortOrder
                      }
                    : undefined
        };
    }

    public navigate({ query, pagination, filters, sortingCriteria }: Partial<SearchParameters>, replaceUrl?: boolean) {
        const getFilter = (filterName: string) => _.get(filters, filterName);
        const getValueIfNotEmpty = (filter) => (!_.isEmpty(filter) ? filter : null);
        const getSortingCriteria = (criteria) => ({
            sortBy: _.get(criteria, 'sortBy'),
            sortOrder: _.get(criteria, 'sortOrder')
        });

        const queryParams = {
            offset: _.get(pagination, 'offset', null),
            ...(!_.isUndefined(query) ? { query } : {}),
            ...(getFilter('brands') ? { brands: getValueIfNotEmpty(filters.brands) } : {}),
            ...(getFilter('brand') || getFilter('brand') === null ? { brand: getValueIfNotEmpty(filters.brand) } : {}),
            ...(getFilter('categories') ? { categories: getValueIfNotEmpty(filters.categories) } : {}),
            ...(getFilter('seasons') ? { seasons: getValueIfNotEmpty(filters.seasons) } : {}),
            ...(getFilter('subSeasons') ? { subSeasons: getValueIfNotEmpty(filters.subSeasons) } : {}),
            ...(getFilter('ageGroups') ? { ageGroups: getValueIfNotEmpty(filters.ageGroups) } : {}),
            ...(getFilter('targetGroups') ? { targetGroups: getValueIfNotEmpty(filters.targetGroups) } : {}),
            ...(getFilter('fits') ? { fits: getValueIfNotEmpty(filters.fits) } : {}),
            ...(getFilter('colors') ? { colors: getValueIfNotEmpty(filters.colors) } : {}),
            ...(getFilter('activeStatuses') ? { activeStatuses: getValueIfNotEmpty(filters.activeStatuses) } : {}),
            ...(getFilter('sizes') ? { sizes: getValueIfNotEmpty(filters.sizes) } : {}),
            ...(getFilter('catalogs') ? { catalogs: getValueIfNotEmpty(filters.catalogs) } : {}),
            ...(getFilter('generalSizes') ? { generalSizes: getValueIfNotEmpty(filters.generalSizes) } : {}),
            ...(getFilter('widths') ? { widths: getValueIfNotEmpty(filters.widths) } : {}),
            ...(getFilter('lengths') ? { lengths: getValueIfNotEmpty(filters.lengths) } : {}),
            ...(getFilter('deliveryDates') ? { deliveryDates: getValueIfNotEmpty(filters.deliveryDates) } : {}),
            ...(getFilter('retailers') ? { retailers: getValueIfNotEmpty(filters.retailers) } : {}),
            ...(getFilter('orderTypes') ? { orderTypes: getValueIfNotEmpty(filters.orderTypes) } : {}),
            ...(getFilter('orderDates') || getFilter('orderDates') === null ? { orderDates: filters.orderDates } : {}),
            ...(!_.isUndefined(getFilter('minimumDiscount')) ? { minimumDiscount: filters.minimumDiscount } : {}),
            ...(!_.isUndefined(getFilter('minimumStock')) ? { minimumStock: filters.minimumStock } : {}),
            ...(getFilter('mainCategory') || getFilter('mainCategory') === null
                ? { mainCategory: getValueIfNotEmpty(filters.mainCategory) }
                : {}),
            ...(getFilter('level1Category') || getFilter('level1Category') === null
                ? { level1Category: getValueIfNotEmpty(filters.level1Category) }
                : {}),
            ...(!_.isUndefined(sortingCriteria) ? getSortingCriteria(sortingCriteria) : {}),
            ...(_.has(filters, 'hasSustainabilityAttributes')
                ? { hasSustainabilityAttributes: filters.hasSustainabilityAttributes ? true : null }
                : {}),
            ...(_.has(filters, 'orderedArticles') ? { orderedArticles: filters.orderedArticles ? true : null } : {}),
            ...(_.has(filters, 'awaitingInitialDelivery')
                ? { awaitingInitialDelivery: filters.awaitingInitialDelivery ? true : null }
                : {}),
            ...(getFilter('period') || getFilter('period') === null
                ? { period: getValueIfNotEmpty(filters.period) }
                : {})
        };

        const navigateExtraParameters: NavigationExtras = replaceUrl
            ? { queryParams, queryParamsHandling: 'merge', replaceUrl: true, relativeTo: this.route }
            : { queryParams, queryParamsHandling: 'merge' };

        this.router.navigate([], navigateExtraParameters);
    }
}
